// Farm API endpoints using RTK Query

import { baseApi } from './baseApi'
import { API_ENDPOINTS } from '@/constants'
import type {
  Farm,
  CreateFarmData,
  UpdateFarmData,
  FarmFilters,
  FarmStatistics,
  ApiResponse,
} from '@/types'

export const farmApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get all farms with filters
    getFarms: builder.query<Farm[], FarmFilters | void>({
      query: (filters) => ({
        url: API_ENDPOINTS.FARMS.BASE,
        params: filters || {},
      }),
      transformResponse: (response: Farm[]) => response,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Farm' as const, id })),
              { type: 'Farm', id: 'LIST' },
            ]
          : [{ type: 'Farm', id: 'LIST' }],
    }),

    // Get farm by ID
    getFarmById: builder.query<Farm, string>({
      query: (id) => API_ENDPOINTS.FARMS.DETAIL(id),
      transformResponse: (response: ApiResponse<Farm>) => response.data,
      providesTags: (_result, _error, id) => [{ type: 'Farm', id }],
    }),

    // Create farm
    createFarm: builder.mutation<Farm, CreateFarmData>({
      query: (data) => ({
        url: API_ENDPOINTS.FARMS.CREATE,
        method: 'POST',
        body: data,
      }),
      transformResponse: (response: ApiResponse<Farm>) => response.data,
      invalidatesTags: [{ type: 'Farm', id: 'LIST' }],
    }),

    // Update farm
    updateFarm: builder.mutation<Farm, { id: string; data: UpdateFarmData }>({
      query: ({ id, data }) => ({
        url: API_ENDPOINTS.FARMS.UPDATE(id),
        method: 'PUT',
        body: data,
      }),
      transformResponse: (response: ApiResponse<Farm>) => response.data,
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Farm', id },
        { type: 'Farm', id: 'LIST' },
      ],
    }),

    // Delete farm
    deleteFarm: builder.mutation<void, string>({
      query: (id) => ({
        url: API_ENDPOINTS.FARMS.DELETE(id),
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Farm', id },
        { type: 'Farm', id: 'LIST' },
      ],
    }),

    // Get farm statistics
    getFarmStatistics: builder.query<FarmStatistics, void>({
      query: () => API_ENDPOINTS.FARMS.STATISTICS,
      transformResponse: (response: ApiResponse<FarmStatistics>) => response.data,
      providesTags: ['Farm'],
    }),
  }),
})

export const {
  useGetFarmsQuery,
  useGetFarmByIdQuery,
  useCreateFarmMutation,
  useUpdateFarmMutation,
  useDeleteFarmMutation,
  useGetFarmStatisticsQuery,
} = farmApi
